import { Table as TableIcon, type LucideIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '~/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';

import {
  useFieldMatrixSummary,
  useFieldSetsNotReportedNote,
} from '../hooks/use-field-matrix-summary';
import { useRunFieldSets } from '../hooks/use-run-field-sets';
import { useRunLabels } from '../hooks/use-run-labels';
import {
  CELL_ICON,
  matrixRow,
  type MatrixRow,
  sendsBasisOf,
  sendsKeySuffix,
} from '../utils/field-matrix';
import { fieldSetFields } from '../utils/field-plan';

import { FieldMatrixDialog } from './field-matrix-dialog';
import { GateContentUnavailable } from './gate-content-unavailable';
import { PaneActionButton } from './pane-action-button';

interface Props {
  runId: string;
  /** Section title; the approval tab names the destination ("What ships to …"). */
  title?: string;
  /** Inside a stage-pane section that already renders the title — keep only the summary line. */
  headingless?: boolean;
  /** Products content generation handled (① ENRICHMENT `in_`) — the matrix's "20/20" denominator;
   *  omitted → the Products column shows the written count alone. */
  productsTotal?: number | null;
}

/** One chip's state, in the field matrix's own vocabulary (its columns and notes). */
type ChipState = 'attention' | 'notSent' | 'written' | 'alwaysOn' | 'plain';

/** Picks ONE state per field from its matrix row — attention first (asked for, nothing written),
 *  then asked-but-not-sent, then written, then always written for this destination. */
function chipState(row: MatrixRow): ChipState {
  if (row.note === 'attention') return 'attention';
  if (row.note === 'dropped') return 'notSent';
  if (row.written === 'yes') return 'written';
  if (row.accepts === 'alwaysOn') return 'alwaysOn';
  return 'plain';
}

/** Chip glyphs = the matrix's own cell glyphs, so the card and the matrix it opens read alike. */
const CHIP_ICON: Record<Exclude<ChipState, 'plain'>, { Icon: LucideIcon; tone: string }> = {
  attention: CELL_ICON.problem,
  notSent: CELL_ICON.no,
  written: CELL_ICON.yes,
  alwaysOn: CELL_ICON.alwaysOn,
};

/** The matrix's four columns, for a chip's tooltip ("You asked for: yes · Delivery sends: …"). */
const COLUMNS = ['asked', 'accepts', 'sent', 'written'] as const;

/**
 * "What ships" (R3): the field matrix's one-line summary, wrapping field chips (icon = the
 * matrix's cell glyph: written / asked for but not written / not sent / always written; the raw
 * code + the four matrix columns in a tooltip) and a legend in the same words. The "Field matrix"
 * button opens the shared `FieldMatrixDialog` (the product page adds a "This product" column).
 */
export function DeliveryFieldSets({
  runId,
  title,
  headingless = false,
  productsTotal = null,
}: Props) {
  const { t } = useTranslation('catalog-enrichment-run');
  const { fieldLabel } = useRunLabels();
  const { data, isLoading, isError } = useRunFieldSets(runId);
  const [matrixOpen, setMatrixOpen] = useState(false);
  const summary = useFieldMatrixSummary(data);
  const notReportedNote = useFieldSetsNotReportedNote(data);
  const heading = title ?? t('fieldSets.title');

  if (isLoading) return <Skeleton className="h-24 w-full" />;
  if (isError || !data) {
    return (
      <section aria-label={heading} className="flex flex-col gap-2">
        {headingless ? null : <h3 className="text-foreground text-sm font-semibold">{heading}</h3>}
        <GateContentUnavailable what={t('fieldSets.what')} />
      </section>
    );
  }

  const fields = fieldSetFields(data);
  const rows = fields.map((field) => matrixRow(data, field));
  const states = new Set(rows.map(chipState));
  // §90 — the chip tooltip names the Sends column exactly as the matrix does (Requested/Delivered).
  const sendsSuffix = sendsKeySuffix(sendsBasisOf(data));
  const columnLabel = (col: (typeof COLUMNS)[number]) =>
    t(col === 'sent' ? `fieldSets.matrix.col.sent${sendsSuffix}` : `fieldSets.matrix.col.${col}`);

  return (
    <section aria-label={heading} className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        {headingless ? null : <h3 className="text-foreground text-sm font-semibold">{heading}</h3>}
        <span className="text-muted-foreground text-xs">{summary}</span>
      </div>
      {/* U6 — `enriched: null`: the summary says "— written"; this says why. */}
      {notReportedNote ? <p className="text-muted-foreground text-xs">{notReportedNote}</p> : null}

      {fields.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('fieldSets.empty')}</p>
      ) : (
        <>
          {/* R7: a connector with many fields scrolls its chips inside the card. */}
          <div className="flex max-h-40 flex-wrap gap-1.5 overflow-y-auto">
            {rows.map((row) => {
              const state = chipState(row);
              const icon = state === 'plain' ? null : CHIP_ICON[state];
              return (
                <Tooltip key={row.field} delayDuration={300}>
                  <TooltipTrigger asChild>
                    <span
                      className={cn(
                        'bg-background inline-flex cursor-default items-center gap-1 rounded-full border px-2 py-0.5 text-xs',
                        state === 'attention' && 'border-warning/60',
                      )}
                    >
                      {icon ? (
                        <icon.Icon aria-hidden="true" className={cn('size-3', icon.tone)} />
                      ) : null}
                      {fieldLabel(row.field)}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="font-mono text-xs">{row.field}</p>
                    <p>
                      {COLUMNS.map(
                        (col) => `${columnLabel(col)}: ${t(`fieldSets.matrix.cell.${row[col]}`)}`,
                      ).join(' · ')}
                    </p>
                    {row.note ? <p>{t(`fieldSets.matrix.note.${row.note}`)}</p> : null}
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>

          {/* Legend in the matrix's words, only for the states this batch actually shows. */}
          <div className="text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
            {(['written', 'attention', 'notSent', 'alwaysOn'] as const)
              .filter((state) => states.has(state))
              .map((state) => {
                const { Icon, tone } = CHIP_ICON[state];
                return (
                  <span key={state} className="inline-flex items-center gap-1">
                    <Icon aria-hidden="true" className={cn('size-3', tone)} />
                    {t(`fieldSets.matrix.legend.${state}`)}
                  </span>
                );
              })}
          </div>
          <div>
            <PaneActionButton
              icon={TableIcon}
              label={t('fieldSets.showMatrix')}
              onClick={() => {
                setMatrixOpen(true);
              }}
            />
          </div>
        </>
      )}

      {/* Q10: the full matrix is a wide table — it opens in a Dialog, never inline inside
          the (often narrow) facts rail. */}
      <FieldMatrixDialog
        open={matrixOpen}
        onOpenChange={setMatrixOpen}
        data={data}
        productsTotal={productsTotal}
      />
      {data.alwaysOn && data.alwaysOn.length > 0 ? (
        <p className="text-muted-foreground text-xs">
          {t('fieldSets.alwaysOn', { fields: data.alwaysOn.join(', ') })}
        </p>
      ) : null}
    </section>
  );
}
